import {
    AvailableSmartAssistantImportDTO,
    ProductListItemDTO,
    SmartAssistantDateFormat,
    SmartAssistantDateFormatsDTO,
    SmartAssistantImportColumnKey,
    SmartAssistantImportStatus,
    SmartAssistantImportSummaryDTO,
    SmartAssistantImportValidationError,
    SmartAssistantType,
    SolutionPartnerIdentificationNumberImportRequestDTO,
    UrbanSportsClubMembershipStatusImportRequestDTO
} from '@iota/inh-core-v1-management-client';
import {
    SMART_ASSISTANT_COLUMN_EXPLANATIONS,
    SMART_ASSISTANT_DATE_COLUMNS,
    SMART_ASSISTANT_RESULT_PAGES,
    SMART_ASSISTANT_TYPE_CONFIGS,
    SMART_ASSISTANT_TYPES_WITH_DATE_FORMATS,
    SmartAssistantColumnRow,
    SmartAssistantProductOption,
    SmartAssistantProductStepStatus,
    SmartAssistantResultPage,
    SmartAssistantStep,
    SmartAssistantValidationResult,
    isCustomVariableColumn,
    sortColumnKeysByGlobalOrder,
    toColumnLabel
} from 'src/app/shared/constants-and-enums/smart-assistant-types';

export interface SmartAssistantImportState {
    steps: SmartAssistantStep[];
    currentStepIndex: number;
    importType: SmartAssistantType | null;
    requiredColumns: SmartAssistantImportColumnKey[];
    optionalColumns: SmartAssistantImportColumnKey[];
    allowedValues: { [key: string]: string[] };
    columnLabels: { [key: string]: string };
    fileHeaders: string[] | null;
    fileError: string | null;
    columnMapping: { [key: string]: string };
    manualColumns: string[];
    dateFormats: { [columnKey: string]: SmartAssistantDateFormat };
    cancelLoading: boolean;

    urbanSportsClubTargetMonth: { year: number; month: UrbanSportsClubMembershipStatusImportRequestDTO.MonthEnum } | null;
    productId: string | null;

    validationResult: SmartAssistantValidationResult | null;
    validationLoading: boolean;

    applyLoading: boolean;
    applyResult: SmartAssistantImportSummaryDTO | null;
    applyDataChanged: boolean;
    apiError: boolean;

    currentImportId: string | null;
}

export function toInitialImportState(availableImport: AvailableSmartAssistantImportDTO): SmartAssistantImportState {
    return {
        steps: SMART_ASSISTANT_TYPE_CONFIGS[availableImport.type]?.steps ?? [],
        currentStepIndex: 0,
        importType: availableImport.type,
        requiredColumns: availableImport.requiredColumns ?? [],
        optionalColumns: availableImport.optionalColumns ?? [],
        allowedValues: availableImport.allowedValues ?? {},
        columnLabels: availableImport.columnLabels ?? {},
        fileHeaders: null,
        fileError: null,
        columnMapping: {},
        manualColumns: [],
        dateFormats: {},
        cancelLoading: false,
        urbanSportsClubTargetMonth: null,
        productId: null,
        validationResult: null,
        validationLoading: false,
        applyLoading: false,
        applyResult: null,
        applyDataChanged: false,
        apiError: false,
        currentImportId: null
    };
}

export function hasAmbiguousDateFormat(result: SmartAssistantValidationResult | null): boolean {
    const entries: { validationError?: SmartAssistantImportValidationError | null }[] = result?.entries ?? [];
    return entries.some((entry) => entry.validationError === SmartAssistantImportValidationError.AMBIGUOUS_DATE_FORMAT);
}

export function withValidatedDateFormats(
    current: { [columnKey: string]: SmartAssistantDateFormat },
    result: SmartAssistantValidationResult
): { [columnKey: string]: SmartAssistantDateFormat } {
    return 'dateFormats' in result && !hasAmbiguousDateFormat(result) ? { ...current, ...result.dateFormats } : current;
}

export function toColumnRows(
    labeledColumns: { key: SmartAssistantImportColumnKey; label: string }[],
    importType: SmartAssistantType | null,
    dateFormats: { [columnKey: string]: SmartAssistantDateFormat }
): SmartAssistantColumnRow[] {
    const supportsDateFormats = !!importType && SMART_ASSISTANT_TYPES_WITH_DATE_FORMATS.includes(importType);
    return labeledColumns.map(({ key, label }) => ({
        key,
        label,
        isDate: supportsDateFormats && SMART_ASSISTANT_DATE_COLUMNS.includes(key),
        dateFormat: dateFormats[key] ?? null
    }));
}

export function toExplanationItems(
    importType: SmartAssistantType | null,
    requiredColumns: SmartAssistantImportColumnKey[],
    optionalColumns: SmartAssistantImportColumnKey[],
    allowedValues: { [key: string]: string[] },
    columnLabels: { [key: string]: string }
): { label: string; description: string }[] {
    const explainedColumns = (importType && SMART_ASSISTANT_TYPE_CONFIGS[importType]?.explainedColumns) ?? [];
    return sortColumnKeysByGlobalOrder([...requiredColumns, ...optionalColumns]).flatMap((key) => {
        const keyAllowedValues = allowedValues[key] ?? [];
        const description =
            keyAllowedValues.length > 0
                ? keyAllowedValues.join(', ')
                : explainedColumns.includes(key)
                  ? SMART_ASSISTANT_COLUMN_EXPLANATIONS[key]
                  : undefined;
        return description ? [{ label: toColumnLabel(key, columnLabels), description }] : [];
    });
}

export function toEnteredColumnMapping(
    state: SmartAssistantImportState,
    storedMapping: { [key: string]: string }
): Pick<SmartAssistantImportState, 'columnMapping' | 'manualColumns'> {
    const fileHeaders = state.fileHeaders ?? [];
    const columns = [...sortColumnKeysByGlobalOrder(state.requiredColumns), ...sortColumnKeysByGlobalOrder(state.optionalColumns)];
    const manualColumns = toRetainedManualColumns(state.manualColumns, state.columnMapping, fileHeaders);
    return {
        columnMapping: toAutoColumnMapping(columns, state.columnLabels, fileHeaders, state.columnMapping, manualColumns, storedMapping),
        manualColumns
    };
}

function toRetainedManualColumns(manualColumns: string[], currentMapping: { [key: string]: string }, fileHeaders: string[]): string[] {
    return manualColumns.filter((key) => !currentMapping[key] || fileHeaders.includes(currentMapping[key]));
}

function toAutoColumnMapping(
    columns: SmartAssistantImportColumnKey[],
    columnLabels: { [key: string]: string },
    fileHeaders: string[],
    currentMapping: { [key: string]: string },
    manualColumns: string[],
    storedMapping: { [key: string]: string }
): { [key: string]: string } {
    const isManual = (key: SmartAssistantImportColumnKey) => manualColumns.includes(key);
    const mapping: { [key: string]: string } = {};
    columns
        .filter((key) => isManual(key) && !!currentMapping[key])
        .forEach((key) => {
            mapping[key] = currentMapping[key];
        });

    const assignIfFree = (key: SmartAssistantImportColumnKey, header: string | undefined): void => {
        if (header && fileHeaders.includes(header) && !Object.values(mapping).includes(header)) {
            mapping[key] = header;
        }
    };
    const derivedColumns = columns.filter((key) => !isManual(key));
    derivedColumns.forEach((key) => assignIfFree(key, toColumnLabel(key, columnLabels)));
    derivedColumns.filter((key) => !mapping[key] && !isCustomVariableColumn(key)).forEach((key) => assignIfFree(key, storedMapping[key]));
    return mapping;
}

export function canProceed(state: SmartAssistantImportState): boolean {
    switch (state.steps[state.currentStepIndex]) {
        case SmartAssistantStep.PRODUCT:
            return true;
        case SmartAssistantStep.URBAN_SPORTS_CLUB_DATE:
            return state.urbanSportsClubTargetMonth?.month != null && state.urbanSportsClubTargetMonth?.year != null;
        case SmartAssistantStep.UPLOAD_FILE:
            return state.fileHeaders != null && state.fileHeaders.length > 0 && state.fileError === null;
        case SmartAssistantStep.ASSIGN_COLUMNS: {
            const requiredKeys = state.requiredColumns ?? [];
            const allMapped = requiredKeys.every((key) => !!state.columnMapping[key]);
            const mappedHeaders = Object.values(state.columnMapping).filter((value) => !!value);
            const noDuplicates = new Set(mappedHeaders).size === mappedHeaders.length;
            return allMapped && noDuplicates;
        }
        case SmartAssistantStep.VALIDATE_DATA:
            return state.validationResult?.canBeApproved === true;
        case SmartAssistantStep.COMPLETION:
            return false;
        default:
            return false;
    }
}

export function toProductOptions(products: ProductListItemDTO[]): SmartAssistantProductOption[] {
    return products
        .filter((product) => !!product.productId)
        .map((product) => {
            const baseLabel = product.name || product.productId;
            return {
                discontinued: !!product.isDiscontinued,
                option: {
                    id: product.productId,
                    label: product.isDiscontinued ? `${baseLabel} (${$localize`Discontinued`})` : baseLabel
                }
            };
        })
        .sort(
            (first, second) =>
                Number(first.discontinued) - Number(second.discontinued) || first.option.label.localeCompare(second.option.label)
        )
        .map(({ option }) => option);
}

export function toProductStepStatus(loading: boolean, error: boolean, optionCount: number): SmartAssistantProductStepStatus {
    if (error) {
        return 'error';
    }
    if (loading) {
        return 'loading';
    }
    return optionCount === 0 ? 'empty' : 'ready';
}

export function toValidatedProductName(result: SmartAssistantValidationResult | null): string | null {
    return result && 'productName' in result && result.productName ? result.productName : null;
}

export function hasNoChanges(result: SmartAssistantValidationResult | null): boolean {
    return result?.status === SmartAssistantImportStatus.VALIDATED && (result.errorEntries ?? 0) === 0 && result.canBeApproved !== true;
}

export function toApplyErrorMessage(dataChanged: boolean): string {
    return dataChanged
        ? $localize`The data has changed since validation. Please upload the file again.`
        : $localize`Please contact our support.`;
}

export function toResultPage(importType: SmartAssistantType | null): SmartAssistantResultPage | null {
    return (importType && SMART_ASSISTANT_RESULT_PAGES[importType]) ?? null;
}

export function toDateFormatsDTO(
    columnMapping: { [key: string]: string },
    dateFormats: { [columnKey: string]: SmartAssistantDateFormat }
): SmartAssistantDateFormatsDTO | undefined {
    const mappedDateFormats = Object.fromEntries(Object.entries(dateFormats).filter(([key]) => !!columnMapping[key]));
    return Object.keys(mappedDateFormats).length > 0 ? { dateFormats: mappedDateFormats } : undefined;
}

export function toIdentificationNumberImportRequest(
    columnMapping: { [key: string]: string },
    productId: string | null
): SolutionPartnerIdentificationNumberImportRequestDTO {
    if (productId) {
        return { columnMapping, productId };
    }
    return { columnMapping };
}
